import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import HomePageMap from '../../pages/HomePageMap'
import ErrorBoundary from '../../components/ErrorBoundary'
import { channelsAPI } from '../../services/api'
import SEOHead from '../../components/SEOHead'
import { generateOrganizationSchema, generateWebSiteSchema } from '../../utils/seo'
import { useImmersiveTabs } from '../../hooks/useImmersiveTabs'
import { useImmersiveChannelState } from '../../hooks/useImmersiveChannelState'
import { useImmersiveTabBar } from '../../hooks/useImmersiveTabBar'
import { useUnreadChannels } from '../../hooks/useUnreadChannels'
import ActivationNudge, { hasNudgeBeenShown } from '../../components/onboarding/ActivationNudge'
import { getOnboardingProgress } from '../../components/onboarding/OnboardingProgress'
import { useMapSpot } from '../../context/MapSpotContext'
import { useVendorStatus } from '../../hooks/useVendorStatus'
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout'

export default function ImmersiveHome() {
  const { i18n } = useTranslation()
  const { isAuthenticated, user } = useAuth()
  const { activateSpotMode } = useMapSpot()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Intent from onboarding modal (track_vendors / im_vendor)
  const [initialIntent, setInitialIntent] = useState(null)
  const intentReadRef = useRef(false)
  
  // Use custom hook for tab management
  const {
    activeTab,
    panelState,
    handleTabClick
  } = useImmersiveTabs({
    defaultTab: location.state?.tab || 'search',
    defaultPanelState: 'minimized',
    pageName: 'home',
    entityTabState: 'expanded', // Home page doesn't have entity-specific behavior
    panelCycle: ['expanded', 'minimized'],
    isAuthenticated
  })
  
  const { isDesktop } = useResponsiveLayout()

  // Use hooks for common functionality
  const { unreadCount: unreadChannelCount, refreshUnreadCount } = useUnreadChannels(isAuthenticated, user)
  const { isVendor } = useVendorStatus()

  // Channel navigation state — shared with ImmersiveEntityDetail via useImmersiveChannelState
  const {
    initialChannelId,
    claimPrefill,
    setClaimPrefill,
    handleTabChangeWithChannel,
    handleChannelSelected,
  } = useImmersiveChannelState({ handleTabClick, userEmail: user?.email })

  // Consume onboarding intent once after login (one-shot).
  // Extracted as a named function so both the mount effect and the
  // 'onboarding:intent_selected' event listener can share the same logic.
  const consumeIntent = useCallback((intent) => {
    if (intentReadRef.current) return
    intentReadRef.current = true
    try { localStorage.removeItem('onboarding_v1_intent') } catch { /* noop */ }
    if (intent === 'im_vendor') {
      // Fetch channels to find the user's default support channel, then navigate directly to it
      channelsAPI.getUserChannels({ silentError: true })
        .then((response) => {
          const channels = response?.data?.channels || []
          // Find the user's OWN support channel:
          // - For admins: ownerEmail is populated for all default channels; match their email
          // - For regular users: ownerEmail is absent; any default channel is theirs
          const supportChannel = channels.find(ch =>
            ch.channelType === 'default' &&
            (!ch.ownerEmail || ch.ownerEmail === user?.email)
          )
          if (supportChannel) {
            handleTabChangeWithChannel('contact', null, supportChannel.id)
          } else {
            // Fallback: just open the contact tab (support channel auto-creates on first load)
            handleTabClick('contact')
          }
        })
        .catch(() => {
          handleTabClick('contact')
        })
    } else if (intent === 'track_vendors') {
      setInitialIntent(intent)
    }
  }, [handleTabClick, handleTabChangeWithChannel, user?.email])

  // Run on auth change (handles intent already in localStorage at login time)
  useEffect(() => {
    if (!isAuthenticated) return
    try {
      const storedIntent = localStorage.getItem('onboarding_v1_intent')
      if (storedIntent) consumeIntent(storedIntent)
    } catch { /* localStorage unavailable */ }
  }, [isAuthenticated, consumeIntent])

  // Also listen for intent selected AFTER this effect already ran (e.g. WelcomeModal shown 700ms later)
  useEffect(() => {
    if (!isAuthenticated) return
    const handleLateIntent = (e) => consumeIntent(e.detail)
    window.addEventListener('onboarding:intent_selected', handleLateIntent)
    return () => window.removeEventListener('onboarding:intent_selected', handleLateIntent)
  }, [isAuthenticated, consumeIntent])

  // ── Activation Nudge ────────────────────────────────────────────────────
  // Track progress in state so nudge re-evaluates when a step completes
  const STEP_EVENT = 'onboarding:step_completed'
  const [nudgeProgress, setNudgeProgress] = useState(getOnboardingProgress)
  useEffect(() => {
    const refresh = () => setNudgeProgress(getOnboardingProgress())
    window.addEventListener(STEP_EVENT, refresh)
    return () => window.removeEventListener(STEP_EVENT, refresh)
  }, [])

  // Determine which nudge to show (only one at a time; spot takes priority)
  const activeNudgeType = (() => {
    if (!isAuthenticated) return null
    if (!nudgeProgress.spotted && !hasNudgeBeenShown('spot')) return 'spot'
    if (!nudgeProgress.favorited && nudgeProgress.spotted && !hasNudgeBeenShown('favorite')) return 'favorite'
    return null
  })()
  const [dismissedNudge, setDismissedNudge] = useState(null)
  const visibleNudge = activeNudgeType !== dismissedNudge ? activeNudgeType : null

  const nudgeActions = useMemo(() => ({
    spot: () => {
      handleTabClick('search', 'minimized')
      setTimeout(() => activateSpotMode(), 300)
    },
    favorite: () => handleTabClick('search', 'expanded'),
  }), [handleTabClick, activateSpotMode])

  // Generate structured data for homepage
  const organizationSchema = generateOrganizationSchema()
  const websiteSchema = generateWebSiteSchema()
  
  // Combine schemas into a single JSON-LD object
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema, websiteSchema]
  }

  // Memoize the tabs element - includes i18n.language to re-render on language change
  const immersiveTabsElement = useImmersiveTabBar({
    activeTab,
    panelState,
    handleTabClick,
    isAuthenticated,
    unreadChannelCount,
    isVendor,
    language: i18n.language,
  })

  return (
    <>
      {/* SEO Meta Tags and Structured Data */}
      <SEOHead
        canonical="/"
        structuredData={structuredData}
      />
      
      <div className="relative w-screen h-screen overflow-hidden">
        {/* Full-screen Map — left edge tracks panel width */}
        <div
          className="absolute top-0 right-0 bottom-0 z-0 transition-[left] duration-300 ease-out"
          style={{ left: isDesktop ? (panelState === 'minimized' ? '64px' : '360px') : 0 }}
        >
          <ErrorBoundary>
            <HomePageMap
              key="home-map"
              componentSource="ImmersiveHome"
              isImmersiveMode={true}
              activeTab={activeTab}
              onTabChange={handleTabChangeWithChannel}
              panelState={panelState}
              immersiveTabs={immersiveTabsElement}
              onUnreadCountChange={refreshUnreadCount}
              initialChannelId={initialChannelId}
              onChannelSelected={handleChannelSelected}
              initialIntent={initialIntent}
              claimPrefill={claimPrefill}
              onClaimPrefillConsumed={() => setClaimPrefill(null)}
            />
          </ErrorBoundary>
        </div>
        {/* Contextual activation nudge — floats over the map */}
        {visibleNudge && (
          <ActivationNudge
            type={visibleNudge}
            onAction={nudgeActions[visibleNudge]}
            onDismiss={() => setDismissedNudge(visibleNudge)}
          />
        )}
      </div>
    </>
  )
}
